import type { LayoutListDirection } from "../../../../util/src/layout/types.js";
import type {
  ComponentNode,
  ContainerNode,
  LayoutListComponentProps,
} from "../layout-types";

export const DEFAULT_LAYOUT_LIST_MAX_ITEMS = 10;

export type { LayoutListDirection };

export const DEFAULT_LAYOUT_LIST_DIRECTION: LayoutListDirection = "column";

export const LAYOUT_LIST_DIRECTIONS: readonly LayoutListDirection[] = [
  "column",
  "row",
  "column-reverse",
  "row-reverse",
];

export function isLayoutListDirectionHorizontal(
  direction: LayoutListDirection = DEFAULT_LAYOUT_LIST_DIRECTION,
): boolean {
  return direction === "row" || direction === "row-reverse";
}

export interface LayoutListRowOffsetInput {
  outerHeight: number;
  outerWidth: number;
}

export interface LayoutListRowWithOffsets extends LayoutListRowOffsetInput {
  offsetX: number;
  offsetY: number;
}

/**
 * Assigns absolute offsets for each row based on the list flow direction.
 */
export function resolveLayoutListRowOffsets<T extends LayoutListRowOffsetInput>(
  rows: T[],
  direction: LayoutListDirection = DEFAULT_LAYOUT_LIST_DIRECTION,
): Array<T & LayoutListRowWithOffsets> {
  if (direction === "column") {
    let offsetY = 0;
    return rows.map((row) => {
      const positioned = { ...row, offsetX: 0, offsetY };
      offsetY += row.outerHeight;
      return positioned;
    });
  }
  if (direction === "column-reverse") {
    const totalHeight = rows.reduce((sum, row) => sum + row.outerHeight, 0);
    let offsetFromBottom = totalHeight;
    return rows.map((row) => {
      offsetFromBottom -= row.outerHeight;
      return { ...row, offsetX: 0, offsetY: offsetFromBottom };
    });
  }
  if (direction === "row") {
    let offsetX = 0;
    return rows.map((row) => {
      const positioned = { ...row, offsetX, offsetY: 0 };
      offsetX += row.outerWidth;
      return positioned;
    });
  }
  const totalWidth = rows.reduce((sum, row) => sum + row.outerWidth, 0);
  let offsetFromRight = totalWidth;
  return rows.map((row) => {
    offsetFromRight -= row.outerWidth;
    return { ...row, offsetX: offsetFromRight, offsetY: 0 };
  });
}

export function createDefaultLayoutListRowTemplate(): ContainerNode {
  return {
    id: crypto.randomUUID(),
    kind: "container",
    layout: "absolute",
    x: 0,
    y: 0,
    width: 800,
    height: 100,
    zIndex: 1,
    children: [],
  };
}

export function createInitialLayoutListProps(): LayoutListComponentProps {
  return {
    mode: "preview",
    maxItems: DEFAULT_LAYOUT_LIST_MAX_ITEMS,
    rowTemplate: createDefaultLayoutListRowTemplate(),
    rowVariants: [],
  };
}

export function getLayoutListDimensions(props: LayoutListComponentProps): {
  width: number;
  height: number;
} {
  const maxItems =
    typeof props.maxItems === "number" && Number.isFinite(props.maxItems)
      ? Math.max(1, Math.round(props.maxItems))
      : DEFAULT_LAYOUT_LIST_MAX_ITEMS;
  const rowWidth = Math.max(1, Math.round(props.rowTemplate.width));
  const rowHeight = Math.max(1, Math.round(props.rowTemplate.height));
  const direction = props.direction ?? DEFAULT_LAYOUT_LIST_DIRECTION;
  if (isLayoutListDirectionHorizontal(direction)) {
    return {
      width: rowWidth * maxItems,
      height: rowHeight,
    };
  }
  return {
    width: rowWidth,
    height: rowHeight * maxItems,
  };
}

export function isLayoutListPreview(node: ComponentNode): boolean {
  if (node.component !== "layoutList") return false;
  const props = node.props as LayoutListComponentProps | undefined;
  return (props?.mode ?? "preview") === "preview";
}
